import { useCallback } from "react";
import styled from "@emotion/styled";

import {
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import type { LocalAudioRecordingBlobRecord } from "@/domains/dexie-db/models/audio-recording";
import {
  AudioRecordingCard,
  AudioRecordingCardBody,
  AudioRecordingCardHeader,
  AudioRecordingCardLabel,
} from "@/pages/audio-recordings/shared/audio-recording-card";
import { useCapturedAudioPlayback } from "@/pages/audio-recordings/shared/use-captured-audio-playback";
import {
  AudioRecordingProcessingStatus,
  AudioRecordingUploadStatus,
} from "@/store/audio-recordings";

export type CapturedAudioUtilitiesCardProps = {
  blobs: LocalAudioRecordingBlobRecord[];
  onDeleteSession?: () => void;
  canDelete?: boolean;
  canPlay?: boolean;
  canUpload?: boolean;
  canTransform?: boolean;
  uploadStatus?: AudioRecordingUploadStatus;
  processingStatus?: AudioRecordingProcessingStatus;
  onTriggerUpload?: () => void;
  onTriggerTransform?: () => void;
  playButtonLabel?: string;
  deleteButtonLabel?: string;
  uploadButtonLabel?: string;
  transformButtonLabel?: string;
};

export const CapturedAudioUtilitiesCard = ({
  blobs,
  onDeleteSession,
  canDelete = true,
  canPlay = true,
  canUpload = false,
  canTransform = false,
  uploadStatus,
  processingStatus,
  onTriggerUpload,
  onTriggerTransform,
  playButtonLabel = "Play Captured Audio",
  deleteButtonLabel = "Delete Session",
  uploadButtonLabel = "Upload",
  transformButtonLabel = "Transform",
}: CapturedAudioUtilitiesCardProps) => {
  const playback = useCapturedAudioPlayback(blobs);

  const handlePlay = useCallback(async () => {
    await playback.play();
  }, [playback]);

  const handleAudioEnd = useCallback(() => {
    playback.clear();
  }, [playback]);

  const handleAudioError = useCallback(() => {
    playback.setError("Failed to play recorded audio.");
    playback.clear();
  }, [playback]);

  return (
    <AudioRecordingCard>
      <AudioRecordingCardHeader title="Captured Audio Utilities" />
      <AudioRecordingCardBody>
        <AudioRecordingCardLabel variant={MDSTextVariant.TextXs}>Utilities</AudioRecordingCardLabel>
        <ButtonRow>
          <MDSButton
            variant={MDSButtonVariant.Subtle}
            size={MDSButtonSize.Small}
            onClick={() => void handlePlay()}
            isDisabled={!canPlay || !blobs.length || playback.isPreparing}
          >
            {playback.isPreparing ? "Preparing…" : playButtonLabel}
          </MDSButton>
          {onTriggerUpload && (
            <MDSButton
              variant={MDSButtonVariant.Emphasis}
              onClick={onTriggerUpload}
              isDisabled={!canUpload}
            >
              {uploadButtonLabel}
            </MDSButton>
          )}
          {onTriggerTransform && (
            <MDSButton
              variant={MDSButtonVariant.Emphasis}
              onClick={onTriggerTransform}
              isDisabled={!canTransform}
            >
              {transformButtonLabel}
            </MDSButton>
          )}
          {onDeleteSession && (
            <MDSButton
              variant={MDSButtonVariant.Danger}
              onClick={onDeleteSession}
              isDisabled={!canDelete}
            >
              {deleteButtonLabel}
            </MDSButton>
          )}
        </ButtonRow>

        {(uploadStatus || processingStatus) && (
          <StatusSummary>
            {uploadStatus && (
              <StatusItem>
                <StatusLabel variant={MDSTextVariant.TextXs}>Upload status</StatusLabel>
                <MDSText variant={MDSTextVariant.TextSm}>{uploadStatus}</MDSText>
              </StatusItem>
            )}
            {processingStatus && (
              <StatusItem>
                <StatusLabel variant={MDSTextVariant.TextXs}>Processing status</StatusLabel>
                <MDSText variant={MDSTextVariant.TextSm}>{processingStatus}</MDSText>
              </StatusItem>
            )}
          </StatusSummary>
        )}

        {playback.error && (
          <StatusMessage tone="error" variant={MDSTextVariant.TextSm}>
            {playback.error}
          </StatusMessage>
        )}
        <HiddenAudioElement
          controls
          ref={playback.audioElementRef}
          src={playback.playbackUrl ?? undefined}
          onEnded={handleAudioEnd}
          onError={handleAudioError}
        />
      </AudioRecordingCardBody>
    </AudioRecordingCard>
  );
};

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const StatusSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
`;

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatusLabel = styled(MDSText)`
  color: ${MDSTextColor.Secondary};
`;

const StatusMessage = styled(MDSText)<{ tone?: "error" }>`
  color: ${({ theme, tone }) =>
    tone === "error" ? theme.colors.text.danger : theme.colors.text.secondary};
`;

const HiddenAudioElement = styled.audio`
  display: none;
`;
